import { laptopLargeMax, largeTabletMax, mobileXXLargeMax } from '@propertypal/shared/src/constants/mediaQueries';
import { Property } from '@propertypal/shared/src/types/property';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import React, { CSSProperties, FunctionComponent, ReactNode, useEffect, useState } from 'react';
import useMediaQuery from '../media-query/useMediaQuery';
import SearchPropertyBox from '../property-box/SearchPropertyBox';
import MillarMcCallWylieAd from '../search/MillarMcCallWylieAd';
import PropertyAd from './PropertyAd';
import GridContainer, { FooterContainer, LowerAdContainer, UpperAdContainer } from './PropertyGrid.style';
import PropertyPlaceholder from './PropertyPlaceholder';

interface Props {
  containerStyle?: CSSProperties;
  ariaLabel?: string;
  properties?: Property[] | null;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  onHideToggle?: () => (hidden: boolean, propertyId: number) => Promise<boolean>;
  onEmailClick?: (property: Property) => void;
  onCallCick?: (property: Property) => void;
  disablePremiumProperties?: boolean;
  locationDetail?: PropertySearchResult['locationDetail'];
  maxColumns?: number;
  showAuctionsLabel?: boolean;
  imageLocator?: string;
  thumbImageLocator?: string;
  popularProperties?: boolean; // on homepage, when laptopLargeMax breakpoint, hides 4th property
  upperMiddleAd?: ReactNode;
  lowerMiddleAd?: ReactNode;
  footer?: ReactNode;
  eagerLoading?: boolean;
  showPremiumAds?: boolean;
  photoScroller?: boolean;
  useAltAd?: boolean;
  handPicked?: boolean;
}

const countPremiumProperties = (total: number, property: Property) => {
  if (property?.premiumListing) {
    return total + 1;
  }

  return total;
};

const PropertyGrid: FunctionComponent<Props> = (props) => {
  const isMobile = useMediaQuery({ query: `(${mobileXXLargeMax})` });
  const isTablet = useMediaQuery({ query: `(${largeTabletMax})` });
  const isLaptop = useMediaQuery({ query: `(${laptopLargeMax})` });
  const totalPremiumProperties = props.properties ? props.properties.reduce(countPremiumProperties, 0) : 0;
  const [emptySlots, setEmptySlots] = useState(0);

  useEffect(() => {
    if (props.properties && props.showPremiumAds) {
      const columns = isMobile ? 1 : isTablet ? 2 : 3;
      const totalSlots = props.properties.length - totalPremiumProperties + totalPremiumProperties * columns;

      if (totalSlots % columns !== 0 && props.properties.length > 3) {
        setEmptySlots(columns - (totalSlots % columns));
      } else {
        setEmptySlots(0);
      }
    }
  }, [isMobile, isTablet, isLaptop, props.properties?.length, totalPremiumProperties]);

  if (!props.properties) {
    return (
      <GridContainer
        style={props.containerStyle}
        maxColumns={props.maxColumns || 3}
        popularProperties={props.popularProperties}
        handPicked={props.handPicked}
      >
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
        <PropertyPlaceholder />
      </GridContainer>
    );
  }

  return (
    <GridContainer
      aria-label={props.ariaLabel}
      style={props.containerStyle}
      maxColumns={props.maxColumns || 3}
      popularProperties={props.popularProperties}
      handPicked={props.handPicked}
    >
      {props.properties.map((property, index) => (
        <SearchPropertyBox
          key={`${property.id}${property.featuredListing}${property.type}`}
          property={property}
          disablePremiumProperties={props.disablePremiumProperties}
          onFavourite={props.onFavourite}
          onHideToggle={props.onHideToggle}
          onEmailClick={props.onEmailClick}
          onCallClick={props.onCallCick}
          showAuctionsLabel={props.showAuctionsLabel}
          imageLocator={props.imageLocator}
          locationDetail={props.locationDetail}
          thumbImageLocator={props.thumbImageLocator}
          loading={props.eagerLoading && index < 4 ? 'eager' : 'lazy'}
          useSlider={props.photoScroller}
        />
      ))}

      {props.upperMiddleAd && <UpperAdContainer>{props.upperMiddleAd}</UpperAdContainer>}

      {props.lowerMiddleAd && <LowerAdContainer>{props.lowerMiddleAd}</LowerAdContainer>}

      {props.footer && <FooterContainer>{props.footer}</FooterContainer>}

      {emptySlots > 0 && props.useAltAd && <MillarMcCallWylieAd slots={emptySlots} />}
      {emptySlots > 0 && !props.useAltAd && <PropertyAd slots={emptySlots} />}
    </GridContainer>
  );
};

export default PropertyGrid;
